// src/composables/useLocation.js
import { ref, computed, onUnmounted } from "vue";

export function useLocation() {
  // State
  const isSupported = ref(false);
  const isTracking = ref(false);
  const permissionStatus = ref("prompt"); // 'granted', 'denied', 'prompt'
  const error = ref(null);
  const watchId = ref(null);

  // Current location data
  const currentLocation = ref({
    latitude: null,
    longitude: null,
    accuracy: null,
    altitude: null,
    altitudeAccuracy: null,
    heading: null,
    speed: null,
    timestamp: null,
  });

  // Location history for session
  const locationHistory = ref([]);

  // Office/Field locations (should come from API/store)
  const officeLocation = ref({
    latitude: 40.7589,
    longitude: -73.9851,
    radius: 100, // meters
  });

  const fieldLocations = ref([
    {
      id: 1,
      name: "Main Construction Site",
      latitude: 40.7589,
      longitude: -73.9851,
      radius: 200,
    },
    {
      id: 2,
      name: "Client Office",
      latitude: 40.7505,
      longitude: -73.9934,
      radius: 100,
    },
  ]);

  // Check if geolocation is supported
  const checkSupport = () => {
    isSupported.value = "geolocation" in navigator;
    return isSupported.value;
  };

  // Request location permission
  const requestPermission = async () => {
    if (!checkSupport()) {
      error.value = "Geolocation not supported";
      return false;
    }

    try {
      // Check if permissions API is available
      if ("permissions" in navigator) {
        const result = await navigator.permissions.query({
          name: "geolocation",
        });
        permissionStatus.value = result.state;

        // Listen for permission changes
        result.addEventListener("change", () => {
          permissionStatus.value = result.state;
        });
      }

      // Try to get current position to trigger permission prompt
      await getCurrentPosition();
      return permissionStatus.value === "granted";
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  // Get current position (one-time)
  const getCurrentPosition = async (options = {}) => {
    if (!checkSupport()) {
      error.value = "Geolocation not supported";
      return null;
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    try {
      error.value = null;
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          ...defaultOptions,
          ...options,
        });
      });

      const locationData = extractLocationData(position);
      currentLocation.value = locationData;

      // Add to history
      addToHistory(locationData);

      return locationData;
    } catch (err) {
      handleError(err);
      return null;
    }
  };

  // Start continuous tracking
  const startTracking = (options = {}) => {
    if (!checkSupport()) {
      error.value = "Geolocation not supported";
      return false;
    }

    if (isTracking.value) {
      console.warn("Location tracking already active");
      return true;
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    };

    try {
      error.value = null;
      watchId.value = navigator.geolocation.watchPosition(
        (position) => {
          const locationData = extractLocationData(position);
          currentLocation.value = locationData;
          addToHistory(locationData);
          isTracking.value = true;
        },
        (err) => {
          handleError(err);
          isTracking.value = false;
        },
        { ...defaultOptions, ...options }
      );

      return true;
    } catch (err) {
      handleError(err);
      return false;
    }
  };

  // Stop tracking
  const stopTracking = () => {
    if (watchId.value !== null) {
      navigator.geolocation.clearWatch(watchId.value);
      watchId.value = null;
    }
    isTracking.value = false;
    error.value = null;
  };

  // Extract location data from position object
  const extractLocationData = (position) => {
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      speed: position.coords.speed,
      timestamp: position.timestamp,
    };
  };

  // Add location to history
  const addToHistory = (locationData) => {
    locationHistory.value.push({
      ...locationData,
      id: Date.now(),
    });

    // Keep only last 100 entries
    if (locationHistory.value.length > 100) {
      locationHistory.value = locationHistory.value.slice(-100);
    }
  };

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Check if location is within geofence
  const isWithinGeofence = (location, center, radius) => {
    if (!location || !center) return false;

    const distance = calculateDistance(
      location.latitude,
      location.longitude,
      center.latitude,
      center.longitude
    );

    return distance <= radius;
  };

  // Computed properties
  const isInOffice = computed(() => {
    if (!currentLocation.value.latitude) return false;
    return isWithinGeofence(
      currentLocation.value,
      officeLocation.value,
      officeLocation.value.radius
    );
  });

  const currentFieldLocation = computed(() => {
    if (!currentLocation.value.latitude) return null;

    return fieldLocations.value.find((field) =>
      isWithinGeofence(currentLocation.value, field, field.radius)
    );
  });

  const distanceFromOffice = computed(() => {
    if (!currentLocation.value.latitude || !officeLocation.value.latitude) {
      return null;
    }

    return calculateDistance(
      currentLocation.value.latitude,
      currentLocation.value.longitude,
      officeLocation.value.latitude,
      officeLocation.value.longitude
    );
  });

  const locationMode = computed(() => {
    if (isInOffice.value) return "office";
    if (currentFieldLocation.value) return "field";
    return "remote";
  });

  const isHighAccuracy = computed(() => {
    return (
      currentLocation.value.accuracy && currentLocation.value.accuracy <= 50
    );
  });

  // Reverse geocoding (get address from coordinates)
  const reverseGeocode = async (latitude, longitude) => {
    try {
      // Using OpenStreetMap Nominatim API (free, no key required)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
      );

      if (!response.ok) throw new Error("Geocoding failed");

      const data = await response.json();
      return {
        displayName: data.display_name,
        address: data.address,
        placeId: data.place_id,
      };
    } catch (err) {
      console.error("Reverse geocoding error:", err);
      return null;
    }
  };

  // Get current address
  const getCurrentAddress = async () => {
    if (!currentLocation.value.latitude) {
      error.value = "No location available";
      return null;
    }

    return await reverseGeocode(
      currentLocation.value.latitude,
      currentLocation.value.longitude
    );
  };

  // Format coordinates for display
  const formatCoordinates = (lat, lon, precision = 6) => {
    return `${lat.toFixed(precision)}, ${lon.toFixed(precision)}`;
  };

  // Format distance for display
  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  };

  // Mock location for testing (development only)
  const setMockLocation = (lat, lon, accuracy = 10) => {
    if (process.env.NODE_ENV !== "development") {
      console.warn("Mock location only available in development");
      return;
    }

    const mockPosition = {
      coords: {
        latitude: lat,
        longitude: lon,
        accuracy: accuracy,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
      },
      timestamp: Date.now(),
    };

    const locationData = extractLocationData(mockPosition);
    currentLocation.value = locationData;
    addToHistory(locationData);
  };

  // Error handler
  const handleError = (err) => {
    console.error("Location error:", err);

    switch (err.code) {
      case err.PERMISSION_DENIED:
        error.value =
          "Location permission denied. Please enable location access.";
        permissionStatus.value = "denied";
        break;
      case err.POSITION_UNAVAILABLE:
        error.value =
          "Location information unavailable. Please check your device settings.";
        break;
      case err.TIMEOUT:
        error.value = "Location request timed out. Please try again.";
        break;
      default:
        error.value = err.message || "An unknown location error occurred.";
    }
  };

  // Cleanup on unmount
  onUnmounted(() => {
    stopTracking();
  });

  return {
    // State
    isSupported,
    isTracking,
    permissionStatus,
    error,
    currentLocation,
    locationHistory,
    officeLocation,
    fieldLocations,

    // Computed
    isInOffice,
    currentFieldLocation,
    distanceFromOffice,
    locationMode,
    isHighAccuracy,

    // Methods
    checkSupport,
    requestPermission,
    getCurrentPosition,
    startTracking,
    stopTracking,
    calculateDistance,
    isWithinGeofence,
    reverseGeocode,
    getCurrentAddress,
    formatCoordinates,
    formatDistance,
    setMockLocation,
  };
}
